/**
 * Trovva — Mobile bottom navigation
 *
 * iOS/Android-style: 4 pinned items + a "More" tab that opens a
 * full bottom sheet with every remaining module the role can access.
 *
 * The first 4 items in the role's permissions.modules list are pinned;
 * everything else (plus section dividers) shows up in the More sheet.
 * This means dragging an item higher in `ROLE_PERMISSIONS[role].modules`
 * promotes it to the bottom bar without code changes here.
 */
import { useEffect } from 'react';
import {
  Home, Users, Wrench, FileText, DollarSign, Plus, Sparkles,
  Building2, Wallet, Settings, ClipboardList, Link, Calendar,
  Target, MoreHorizontal, X, Sun, UserPlus,
} from 'lucide-react';
import { theme } from '../../lib/theme';
import { getNavItems } from '../../lib/permissions';

const ICON_MAP = {
  Home, Users, Wrench, FileText, DollarSign, Settings, Sparkles,
  Building: Building2, Wallet, ClipboardList, Link, Calendar, Target, Sun, UserPlus,
};

const PINNED_COUNT = 4;

export default function MobileNav({ activeTab, setActiveTab, userRole, moreOpen, setMoreOpen }) {
  const role = userRole || 'broker';
  const nav = getNavItems(role);
  const pinnable = nav.filter(item => item.type !== 'divider');
  const pinned = pinnable.slice(0, PINNED_COUNT);
  const overflowIds = new Set(pinnable.slice(PINNED_COUNT).map(i => i.id));
  // For the sheet, keep the role's original order (with dividers), but only
  // show items that aren't pinned. Dividers come along to preserve grouping.
  const sheetItems = nav.filter(item =>
    item.type === 'divider' ? true : overflowIds.has(item.id)
  );

  // If the active tab is in overflow, surface that on the More button so the
  // user knows where they are.
  const overflowActive = overflowIds.has(activeTab);

  return (
    <>
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        height: 60,
        backgroundColor: theme.colors.card,
        borderTop: `1px solid ${theme.colors.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-around',
        zIndex: 100,
        paddingBottom: 'env(safe-area-inset-bottom)',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.05)',
      }}>
        {pinned.map(item => {
          const active = activeTab === item.id;
          const IconComponent = ICON_MAP[item.icon] || Home;
          return (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setMoreOpen(false); }}
              style={navBtn(active)}
            >
              <IconComponent size={20} />
              <span style={{ fontSize: 10, fontWeight: active ? 600 : 400 }}>
                {item.mobileLabel || item.label}
              </span>
            </button>
          );
        })}
        <button
          onClick={() => setMoreOpen(!moreOpen)}
          style={navBtn(moreOpen || overflowActive)}
        >
          <MoreHorizontal size={20} />
          <span style={{ fontSize: 10, fontWeight: (moreOpen || overflowActive) ? 600 : 400 }}>
            More
          </span>
        </button>
      </div>

      {moreOpen && (
        <MoreSheet
          items={sheetItems}
          activeTab={activeTab}
          onPick={(id) => { setActiveTab(id); setMoreOpen(false); }}
          onClose={() => setMoreOpen(false)}
        />
      )}
    </>
  );
}

function navBtn(active) {
  return {
    background: 'none', border: 'none', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    padding: '6px 10px', flex: 1,
    color: active ? theme.colors.primary : theme.colors.textLight,
  };
}

/**
 * Bottom-sheet drawer listing all overflow items (with section dividers).
 * Tap-outside or swipe-down on the handle dismisses it.
 */
function MoreSheet({ items, activeTab, onPick, onClose }) {
  // Lock body scroll while open
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.45)', zIndex: 200,
        display: 'flex', alignItems: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          background: theme.colors.card,
          borderTopLeftRadius: 18, borderTopRightRadius: 18,
          padding: '8px 0 max(16px, env(safe-area-inset-bottom)) 0',
          maxHeight: '80vh', overflowY: 'auto',
          boxShadow: '0 -8px 30px rgba(0,0,0,0.18)',
        }}
      >
        {/* Drag handle */}
        <div style={{
          width: 38, height: 4, borderRadius: 2,
          background: theme.colors.border,
          margin: '6px auto 12px',
        }} />

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0 16px 8px',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            More
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: theme.colors.textLight, padding: 6,
          }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {items.map((item, idx) => {
            if (item.type === 'divider') {
              return (
                <div key={item.id || `d-${idx}`} style={{
                  padding: '14px 16px 6px',
                  borderTop: idx === 0 ? 'none' : `1px solid ${theme.colors.border}`,
                }}>
                  <div style={{
                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                    color: theme.colors.textLight, letterSpacing: '0.08em',
                  }}>
                    {item.label}
                  </div>
                </div>
              );
            }
            const IconComponent = ICON_MAP[item.icon] || Home;
            const active = item.id === activeTab;
            return (
              <button
                key={item.id}
                onClick={() => onPick(item.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 18px', border: 'none', cursor: 'pointer', textAlign: 'left',
                  background: active ? 'rgba(27,94,84,0.08)' : 'transparent',
                  color: active ? theme.colors.primary : theme.colors.text,
                  fontSize: 15, fontWeight: active ? 600 : 500,
                }}
              >
                <IconComponent size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function FloatingActionButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        position: 'fixed', bottom: 76, right: 16,
        width: 50, height: 50, borderRadius: 25,
        background: theme.colors.primary,
        border: 'none', color: 'white', cursor: 'pointer',
        boxShadow: theme.shadow.lg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 90,
      }}
    >
      <Plus size={24} />
    </button>
  );
}
